import React, { useEffect, useState } from "react";
import KlubbVelger, { getKlubbId } from "./KlubbVelger";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function Lager() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [form, setForm] = useState({ productName: "", ean: "", quantity: "", note: "" });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  async function load() {
    try {
      const res = await fetch(`${API_BASE_URL}/klubbsystem/inventory?clubId=${getKlubbId()}`);
      if (!res.ok) throw new Error("Kunne ikke hente lagerbeholdning");
      const data = await res.json();
      data.sort((a, b) => a.productName.localeCompare(b.productName));
      setRows(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.productName.trim() || !form.quantity) return;
    setSaving(true);
    setSaveError("");
    try {
      const res = await fetch(`${API_BASE_URL}/klubbsystem/inventory`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clubId: getKlubbId(),
          productName: form.productName.trim(),
          ean: form.ean.trim(),
          quantity: Number(form.quantity),
          note: form.note,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Kunne ikke registrere varemottak");
      }
      setForm({ productName: "", ean: "", quantity: "", note: "" });
      await load();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto py-10 px-4">
      <span className="inline-block px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold mb-3">
        Klubb
      </span>
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">Klubblager</h1>
      <p className="text-gray-500 mb-6">
        Hva klubben og hvert lag har på lager. Klubbadministrator fordeler varer mellom klubb og lag i klubbsport1 —
        her registrerer du varemottak inntil fakturaimport er på plass.
      </p>

      <KlubbVelger onChange={load} />

      {loading && <p className="text-gray-400 text-sm">Laster …</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!loading && !error && (
        rows.length === 0 ? (
          <p className="text-gray-400 text-sm mb-6">Ingen varer registrert ennå.</p>
        ) : (
          <div className="border border-gray-200 rounded-xl overflow-hidden mb-8">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-2.5">Produkt</th>
                    <th className="text-left px-4 py-2.5">Størrelse</th>
                    <th className="text-left px-4 py-2.5">Varenummer</th>
                    <th className="text-left px-4 py-2.5">EAN</th>
                    <th className="text-left px-4 py-2.5">Mottaker</th>
                    <th className="text-right px-4 py-2.5">Antall</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="px-4 py-2.5 font-medium text-gray-900">{row.productName}</td>
                      <td className="px-4 py-2.5 text-gray-500">{row.size || "—"}</td>
                      <td className="px-4 py-2.5 text-gray-500">{row.sku || "—"}</td>
                      <td className="px-4 py-2.5 text-gray-500">{row.ean || "—"}</td>
                      <td className={`px-4 py-2.5 ${row.teamName || row.departmentName ? "text-gray-900" : "text-gray-400"}`}>
                        {row.teamName || (row.departmentName ? `${row.departmentName} (ikke fordelt)` : "Ufordelt")}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{row.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      <div className="border border-gray-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Registrer varemottak</h2>
        <form onSubmit={handleSubmit} className="flex flex-wrap gap-2">
          <input
            className="flex-1 min-w-[160px] border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Produktnavn"
            value={form.productName}
            onChange={(e) => setForm((f) => ({ ...f, productName: e.target.value }))}
            required
          />
          <input
            className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="EAN"
            value={form.ean}
            onChange={(e) => setForm((f) => ({ ...f, ean: e.target.value }))}
          />
          <input
            className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Antall"
            type="number"
            value={form.quantity}
            onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
            required
          />
          <input
            className="flex-1 min-w-[160px] border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Notat, f.eks. fakturanummer"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
          />
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold disabled:opacity-50"
          >
            {saving ? "Lagrer …" : "Registrer"}
          </button>
        </form>
        {saveError && <p className="text-red-600 text-sm mt-2">{saveError}</p>}
      </div>
    </div>
  );
}
